import * as React from 'react'

import { ResourceCard } from '~/client/src/shared/components/ResourceCardList/ResourceCard'
import { ResourceLink } from '~/client/src/shared/components/ResourceLink'
import { IResourceLinkFieldResult } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'

import { ResourceEdit } from './ResourceEdit'
import { useResourceEditState } from './ResourceEditContext'
import { IResourceEditSection } from './ResourceEditSections'

interface IProps {
  isCreationMode: boolean
  /**
   * The resource's link to another resource. Optional: only equipment links to a
   * StruxTag, and a type without one renders no link context at all.
   */
  linkField?: IResourceLinkFieldResult
  onOpenLinked?: () => void
  /** Property sections rendered as a secondary nav under the Properties tab. */
  sections: IResourceEditSection[]
  /** Rendered in creation mode (e.g. a form/template picker). */
  formSelector?: React.ReactNode
  /**
   * Show the link row — the "Link to X" button when nothing is linked, or the
   * "Linked to X" header with its Change/Unlink buttons and the linked card.
   * Defaults to true; callers pass false when the linked resource's app is
   * turned off for the project, which leaves the card with no way to link.
   */
  showLink?: boolean
}

export const ResourceEditDetailsBody: React.FC<IProps> = ({
  isCreationMode,
  linkField,
  onOpenLinked,
  sections,
  formSelector,
  showLink = true,
}) => {
  const { isLocked } = useResourceEditState()
  const isLinkVisible = !isCreationMode && showLink && !!linkField

  const content = (
    <>
      <ResourceEdit.Header />
      <ResourceEdit.Content>
        <ResourceEdit.QRCode />
        {isCreationMode && formSelector}
        {isLinkVisible && (
          <ResourceLink.Trigger
            label={linkField.triggerLabel}
            cardModel={linkField.linkedCardModel}
            leadingIcon={linkField.linkedLeadingIcon ?? linkField.leadingIcon}
            onOpenLinked={onOpenLinked}
            readOnly={isLocked}
          />
        )}
        <ResourceEdit.Sections sections={sections} />
      </ResourceEdit.Content>
      <ResourceEdit.Footer />
      {isLinkVisible && (
        <ResourceLink.Picker
          title={linkField.pickerTitle}
          records={linkField.targetRecords}
          resourceTypeId={linkField.targetResourceTypeId}
          cardConfiguration={linkField.targetCardConfiguration}
          properties={linkField.targetProperties}
          getDisplayValue={linkField.getTargetDisplayValue}
          emptyMessage={linkField.emptyMessage}
        >
          {(record, model) => (
            <ResourceCard.Standard
              model={model}
              leading={renderResourceRecordIcon(
                linkField.targetResourceTypeId,
                record,
                linkField.leadingIcon,
                'tw:block tw:size-full tw:text-muted-foreground',
              )}
            />
          )}
        </ResourceLink.Picker>
      )}
    </>
  )

  // Only wrap in the link context when there is a link. ResourceLink.Root spreads
  // linkField.linkRootProps, so a type without one (every resource but equipment)
  // would throw on render rather than simply showing no link row.
  return linkField ? (
    <ResourceLink.Root {...linkField.linkRootProps}>
      {content}
    </ResourceLink.Root>
  ) : (
    content
  )
}
